import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useIsMobile, useDeviceType } from "../../App";

const ChatSearchModal = ({ isOpen, onClose, onSelectChat, chats = [] }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);

  const { theme } = useTheme();
  const { t, currentLanguage } = useLocalization();
  const isMobile = useIsMobile();
  const deviceType = useDeviceType();

  const localeMap = {
    uk: "uk-UA",
    en: "en-US",
    ru: "ru-RU",
    pl: "pl-PL",
    es: "es-ES",
    pt: "pt-PT",
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Search functionality
  const searchInText = (text, query) => {
    if (!query.trim()) return false;
    return text.toLowerCase().includes(query.toLowerCase().trim());
  };

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      // Show recent chats when no search query
      return chats.slice(0, 8).map((chat) => ({
        chat,
        titleMatch: false,
        messageMatches: [],
        totalMatches: 0,
        isRecent: true,
      }));
    }

    const results = [];

    chats.forEach((chat) => {
      const titleMatch = searchInText(
        chat.title || chat.name || "",
        searchQuery,
      );
      const messageMatches = (chat.messages || []).filter((message) =>
        searchInText(message.text || "", searchQuery),
      );

      if (titleMatch || messageMatches.length > 0) {
        results.push({
          chat,
          titleMatch,
          messageMatches,
          totalMatches: (titleMatch ? 1 : 0) + messageMatches.length,
          isRecent: false,
        });
      }
    });

    return results
      .sort((a, b) => {
        if (a.totalMatches !== b.totalMatches) {
          return b.totalMatches - a.totalMatches;
        }
        return (
          new Date(b.chat.updatedAt || b.chat.createdAt) -
          new Date(a.chat.updatedAt || a.chat.createdAt)
        );
      })
      .slice(0, 10);
  }, [chats, searchQuery]);

  const highlightText = (text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(
      `(${query.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    return text.replace(
      regex,
      '<mark style="background-color: #ffd60a; color: #000; padding: 1px 2px; border-radius: 2px; font-weight: 600;">$1</mark>',
    );
  };

  const getSearchSnippet = (text, query, maxLength = 80) => {
    if (!query.trim())
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase().trim();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1)
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;

    const start = Math.max(0, index - 25);
    const end = Math.min(text.length, index + query.length + 25);

    let snippet = text.substring(start, end);
    if (start > 0) snippet = "..." + snippet;
    if (end < text.length) snippet = snippet + "...";

    return snippet;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return t("justNow") || "Just now";
    } else if (diffInHours < 24) {
      return (
        t("hoursAgo", { hours: Math.floor(diffInHours) }) ||
        `${Math.floor(diffInHours)} hours ago`
      );
    } else if (diffInHours < 48) {
      return t("yesterday") || "Yesterday";
    } else {
      return date.toLocaleDateString(localeMap[currentLanguage] || "en-US", {
        day: "numeric",
        month: "short",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (searchResults[selectedIndex]) {
        handleSelectChat(searchResults[selectedIndex].chat);
      }
    }
  };

  const handleSelectChat = (chat) => {
    onSelectChat(chat);
    onClose();
  };

  // Update selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  if (!isOpen) return null;

  // Styles
  const overlayStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 9999,
    display: "flex",
    alignItems: deviceType === 'mobile' ? "flex-end" : deviceType === 'tablet' ? "center" : "flex-start",
    justifyContent: "center",
    padding: deviceType === 'mobile' ? "0" : deviceType === 'tablet' ? "5vh 16px" : "10vh 16px 16px",
  };

  const modalStyles = {
    width: deviceType === 'mobile' ? "100%" : "90%",
    maxWidth: "640px",
    backgroundColor: theme.surface,
    borderRadius: deviceType === 'mobile' ? "16px 16px 0 0" : "12px",
    boxShadow: theme.isDark
      ? "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)"
      : "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
    maxHeight: deviceType === 'mobile' ? "90vh" : deviceType === 'tablet' ? "60vh" : "70vh",
    height: deviceType === 'mobile' ? "90vh" : "auto",
    display: "flex",
    flexDirection: "column",
    margin: 0,
  };

  const headerStyles = {
    padding: isMobile ? "16px" : "20px 24px 16px",
    borderBottom: `1px solid ${theme.border}`,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "12px" : "14px",
  };

  const searchInputStyles = {
    width: "100%",
    padding: isMobile ? "10px 14px" : "12px 16px",
    fontSize: isMobile ? "16px" : "16px",
    border: `1px solid ${theme.border}`,
    outline: "none",
    backgroundColor: theme.background,
    color: theme.text,
    borderRadius: "8px",
    transition: "border-color 0.2s ease",
    WebkitAppearance: "none",
  };

  const resultsContainerStyles = {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    WebkitOverflowScrolling: "touch",
  };

  const headerTopRowStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  };

  const headerTitleStyles = {
    fontSize: isMobile ? "16px" : "18px",
    fontWeight: "600",
    color: theme.text,
    margin: 0,
  };

  const closeButtonStyles = {
    border: "none",
    background: theme.isDark ? "#2a2a2a" : "#f0f0f0",
    color: theme.text,
    width: isMobile ? "36px" : "32px",
    height: isMobile ? "36px" : "32px",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    lineHeight: 1,
    transition: "opacity 0.2s ease",
    flexShrink: 0,
  };

  const noResultsStyles = {
    padding: isMobile ? "40px 16px" : "60px 24px",
    textAlign: "center",
    color: theme.textSecondary,
  };

  const noResultsIconStyles = {
    fontSize: "48px",
    marginBottom: "16px",
    opacity: 0.5,
  };

  const recentHeaderStyles = {
    padding: isMobile ? "12px 16px 8px" : "16px 24px 8px",
    fontSize: "14px",
    fontWeight: "600",
    color: theme.textSecondary,
    backgroundColor: theme.background,
    borderBottom: `1px solid ${theme.border}`,
    flexShrink: 0,
  };

  const resultItemStyles = (isSelected, isRecent) => ({
    padding: isMobile ? "12px 16px" : "12px 24px",
    cursor: "pointer",
    backgroundColor: isSelected
      ? theme.isDark
        ? "#2a2a2a"
        : "#f0f0f0"
      : "transparent",
    borderBottom: `1px solid ${theme.border}`,
    transition: "background-color 0.1s ease",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    WebkitTapHighlightColor: "transparent",
  });

  const resultTitleStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  };

  const chatTitleStyles = {
    fontSize: "15px",
    fontWeight: "500",
    color: theme.text,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const matchInfoStyles = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: theme.textSecondary,
  };

  const matchBadgeStyles = {
    backgroundColor: theme.primary + "20",
    color: theme.primary,
    padding: "2px 6px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: "500",
  };

  const messagePreviewStyles = {
    fontSize: "13px",
    color: theme.textSecondary,
    lineHeight: "1.4",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };

  const authorBadgeStyles = {
    fontSize: "11px",
    fontWeight: "600",
    color: theme.primary,
    backgroundColor: theme.primary + "15",
    padding: "1px 4px",
    borderRadius: "4px",
    flexShrink: 0,
  };

  const dateStyles = {
    fontSize: "11px",
    color: theme.textSecondary,
    fontWeight: "400",
  };

  const shortcutHintStyles = {
    padding: "12px 24px",
    fontSize: "12px",
    color: theme.textSecondary,
    backgroundColor: theme.background,
    borderTop: `1px solid ${theme.border}`,
    display: "flex",
    justifyContent: "center",
    gap: "16px",
  };

  const shortcutStyles = {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  const keyStyles = {
    padding: "2px 6px",
    backgroundColor: theme.isDark ? "#3a3a3a" : "#e5e5e5",
    borderRadius: "4px",
    fontFamily: "monospace",
    fontSize: "11px",
  };

  return (
    <div style={overlayStyles} onClick={onClose}>
      <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyles}>
          <div style={headerTopRowStyles}>
            <h2 style={headerTitleStyles}>
              {searchQuery
                ? t("searchResults") || "Search Results"
                : t("searchChats") || "Search Chats"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              style={closeButtonStyles}
              aria-label={t("close") || "Close"}
            >
              ×
            </button>
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("searchChats") || "Search in your chats..."}
            style={searchInputStyles}
          />
        </div>

        {/* Results */}
        <div style={resultsContainerStyles} ref={resultsRef}>
          {searchResults.length === 0 && searchQuery ? (
            <div style={noResultsStyles}>
              <div style={noResultsIconStyles}>🔍</div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                {t("noSearchResults") || "No results found"}
              </div>
              <div style={{ fontSize: "14px" }}>
                {t("tryDifferentKeywords") || "Try different keywords"}
              </div>
            </div>
          ) : (
            <>
              {!searchQuery && (
                <div style={recentHeaderStyles}>
                  {t("recentChats") || "Recent Chats"}
                </div>
              )}

              {searchResults.map((result, index) => {
                const { chat, messageMatches, totalMatches, isRecent } = result;

                return (
                  <div
                    key={chat.id}
                    style={resultItemStyles(index === selectedIndex, isRecent)}
                    onClick={() => handleSelectChat(chat)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div style={resultTitleStyles}>
                      <div
                        style={chatTitleStyles}
                        dangerouslySetInnerHTML={{
                          __html: highlightText(
                            chat.title ||
                              chat.name ||
                              t("newChat") ||
                              "New Chat",
                            searchQuery,
                          ),
                        }}
                      />
                      <div style={matchInfoStyles}>
                        {!isRecent && totalMatches > 0 && (
                          <span style={matchBadgeStyles}>
                            {totalMatches}{" "}
                            {totalMatches === 1
                              ? t("match") || "match"
                              : t("matches") || "matches"}
                          </span>
                        )}
                        <span style={dateStyles}>
                          {formatDate(chat.updatedAt || chat.createdAt)}
                        </span>
                      </div>
                    </div>

                    {/* Message previews for search results */}
                    {messageMatches.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {messageMatches.slice(0, 2).map((message, msgIndex) => (
                          <div
                            key={message.id || msgIndex}
                            style={messagePreviewStyles}
                          >
                            <span style={authorBadgeStyles}>
                              {message.isUser ? t("you") || "You" : "AI"}
                            </span>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: highlightText(
                                  getSearchSnippet(
                                    message.text,
                                    searchQuery,
                                    60,
                                  ),
                                  searchQuery,
                                ),
                              }}
                            />
                          </div>
                        ))}
                        {messageMatches.length > 2 && (
                          <div
                            style={{
                              ...messagePreviewStyles,
                              fontStyle: "italic",
                              color: theme.primary,
                            }}
                          >
                            +{messageMatches.length - 2}{" "}
                            {t("moreMatches") || "more matches"}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show last message for recent chats without search */}
                    {isRecent && chat.messages && chat.messages.length > 0 && (
                      <div style={messagePreviewStyles}>
                        <span style={authorBadgeStyles}>
                          {chat.messages[chat.messages.length - 1].isUser
                            ? t("you") || "You"
                            : "AI"}
                        </span>
                        <span>
                          {chat.messages[chat.messages.length - 1].text.length >
                          60
                            ? chat.messages[
                                chat.messages.length - 1
                              ].text.substring(0, 60) + "..."
                            : chat.messages[chat.messages.length - 1].text}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Keyboard shortcuts hint - hidden on mobile and tablet */}
        {deviceType === 'desktop' && (
          <div style={shortcutHintStyles}>
            <div style={shortcutStyles}>
              <span style={keyStyles}>↑↓</span>
              <span>{t("navigate") || "Navigate"}</span>
            </div>
            <div style={shortcutStyles}>
              <span style={keyStyles}>Enter</span>
              <span>{t("select") || "Select"}</span>
            </div>
            <div style={shortcutStyles}>
              <span style={keyStyles}>Esc</span>
              <span>{t("close") || "Close"}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSearchModal;
